// pages/Dashboard/VendaProdutos.jsx
import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Skeleton,
  Button,
  Stack,
  IconButton,
  Tooltip as MuiTooltip,
} from "@mui/material";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { DataGrid } from "@mui/x-data-grid";
import TableChartIcon from "@mui/icons-material/TableChart";

import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import { useDashboard } from "../hooks/useDashboard";
import localeTextPTBR from "../utils/localeTextPTBR";
import * as XLSX from "xlsx";
import { useMutation } from "react-query";
import MetricAiInsightsDialog from "./MetricAiInsightsDialog";
import { getDashboardMetricInsights } from "../api/client";

/* Cores fortes — KPIs */
const COLORS = {
  primary: "#0B60B0",
  accent: "#00B3C6",
  warm: "#FFB300",
  warn: "#FF7043",
};

/* Tons pastéis — SOMENTE gráficos */
const CHART_SERIES = [
  "#8CB4E8",
  "#90E0EF",
  "#F6D28F",
  "#F7B5A8",
  "#B9A7E8",
  "#A8E6CF",
  "#F6B6D8",
  "#D7CCC8",
];

const BRL = (v) =>
  (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

/** Exporta a tabela de vendas para Excel respeitando os dados já filtrados (data início/fim e unidade do dashboard) */
const exportSalesToExcel = (salesTable, start, end) => {
  if (!salesTable?.length) {
    alert("Não há dados para exportar no período selecionado.");
    return;
  }
  const headers = [
    "Nº Pedido",
    "Local da Venda",
    "Local da Fatura",
    "Status / Expedição",
    "Valor (R$)",
    "Cód. Vendedor",
  ];
  const rows = salesTable.map((row) => [
    row.orderNumber ?? "",
    row.saleLocation ?? "",
    row.invoiceLocation ?? "",
    row.status ?? "",
    typeof row.value === "number" ? row.value : Number(row.value) || 0,
    row.sellerCode ?? "",
  ]);
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  const colWidths = [{ wch: 12 }, { wch: 28 }, { wch: 32 }, { wch: 36 }, { wch: 14 }, { wch: 14 }];
  ws["!cols"] = colWidths;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Vendas");
  const stamp = `${(start || "").replace(/-/g, "")}_${(end || "").replace(/-/g, "")}`;
  XLSX.writeFile(wb, `detalhamento_vendas_${stamp}.xlsx`);
};

/* Layout helpers */
const KPI_MIN_H = 148;
const CHART_MIN_H = 360;
const CARD_STRETCH = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minHeight: KPI_MIN_H,
};
const CHART_CARD = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minHeight: CHART_MIN_H,
};

// Tick do eixo X com quebra automática (até ~16 chars/linha)
const WrapTick = ({ x, y, payload }) => {
  const max = 16;
  const words = String(payload?.value ?? "").split(" ");
  const lines = [];
  let line = "";
  words.forEach((w) => {
    const test = line ? `${line} ${w}` : w;
    if (test.length > max) {
      if (line) lines.push(line);
      line = w;
    } else {
      line = test;
    }
  });
  if (line) lines.push(line);
  return (
    <g transform={`translate(${x}, ${y + 10})`}>
      <text textAnchor="middle" fontSize={12} fill="#333">
        {lines.map((l, i) => (
          <tspan key={i} x="0" dy={i === 0 ? 0 : 14}>
            {l}
          </tspan>
        ))}
      </text>
    </g>
  );
};

const KpiCard = ({ icon, title, value, color, sub, loading }) => (
  <Card sx={CARD_STRETCH}>
    <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      {loading ? (
        <Skeleton variant="circular" width={44} height={44} />
      ) : (
        <Avatar sx={{ bgcolor: color, width: 44, height: 44 }}>{icon}</Avatar>
      )}
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {title}
        </Typography>
        {loading ? (
          <Skeleton height={36} width="60%" />
        ) : (
          <Typography variant="h4" sx={{ color }}>
            {value}
          </Typography>
        )}
        {sub && (
          <Typography variant="caption" color="text.secondary">
            {sub}
          </Typography>
        )}
      </Box>
    </CardContent>
  </Card>
);

const VendaProdutos = ({ start, end }) => {
  const params = {
    start: start,
    end: end,
  };

  const { data, isLoading, error } = useDashboard("vendas", "charts", params, {
    select: (resp) => resp?.data || resp || {},
  });

  const cards = data?.cards || {};
  const charts = data?.charts || {};

  const revenueByCategory = charts?.revenueByCategory || [];
  const salesByChannel = charts?.salesByChannel || [];
  const monthlyOverview = charts?.monthlyOverview || [];
  const salesTable = charts?.salesTable || [];

  const [metricDialogOpen, setMetricDialogOpen] = React.useState(false);
  const [metricDialogMetric, setMetricDialogMetric] = React.useState(null);
  const [metricDialogName, setMetricDialogName] = React.useState("");
  const [metricDialogLabel, setMetricDialogLabel] = React.useState("");
  const [metricDialogData, setMetricDialogData] = React.useState(null);
  const [metricDialogError, setMetricDialogError] = React.useState(null);
  const [metricDialogHuman, setMetricDialogHuman] = React.useState("");
  const [metricDialogBasePayload, setMetricDialogBasePayload] = React.useState(null);
  const lastCfgRef = React.useRef(null);

  const metricInsightsMutation = useMutation(
    async ({ metric, metricName, metricDefinition, metricValue, metricHuman }) => {
      const payload = {
        metric,
        metricName,
        metricDefinition,
        metricValue,
        metricHuman,
        period: { start, end },
      };
      return getDashboardMetricInsights("vendas", payload);
    },
    {
      onSuccess: (data) => {
        setMetricDialogData(data || null);
        setMetricDialogError(null);
      },
      onError: (err) => {
        const msg =
          (typeof err?.message === "string" && err.message) ||
          (typeof err?.error === "string" && err.error) ||
          "Não foi possível gerar a explicação da IA no momento.";
        setMetricDialogError(msg);
      },
    }
  );

  const openAi = (cfg) => {
    setMetricDialogMetric(cfg.metric);
    setMetricDialogName(cfg.metricName || "");
    setMetricDialogLabel(cfg.metricLabel || "");
    setMetricDialogHuman(cfg.metricHuman || "");
    setMetricDialogBasePayload(cfg);
    setMetricDialogOpen(true);
    setMetricDialogData(null);
    setMetricDialogError(null);
    lastCfgRef.current = cfg;
    metricInsightsMutation.mutate(cfg);
  };

  const AiBtn = ({ cfg }) => (
    <MuiTooltip title="Explicar com IA">
      <IconButton size="small" onClick={() => openAi(cfg)} aria-label="Explicar com IA">
        <AutoAwesomeIcon fontSize="inherit" />
      </IconButton>
    </MuiTooltip>
  );

  const salesGridColumns = useMemo(
    () => [
      { field: "orderNumber", headerName: "Nº Pedido", flex: 0.8, minWidth: 90 },
      { field: "saleLocation", headerName: "Local da Venda", flex: 1.2, minWidth: 180 },
      { field: "invoiceLocation", headerName: "Local da Fatura", flex: 1.2, minWidth: 200 },
      { field: "status", headerName: "Status / Expedição", flex: 1.4, minWidth: 220 },
      {
        field: "value",
        headerName: "Valor (R$)",
        flex: 0.8,
        minWidth: 110,
        type: "number",
        valueFormatter: (params) => BRL(params?.value),
      },
      { field: "sellerCode", headerName: "Cód. Vendedor", flex: 0.8, minWidth: 110 },
    ],
    []
  );

  const salesGridRows = useMemo(
    () =>
      (salesTable || []).map((row, index) => ({
        id: row.orderNumber != null ? `order-${row.orderNumber}-${index}` : `row-${index}`,
        orderNumber: row.orderNumber,
        saleLocation: row.saleLocation ?? "-",
        invoiceLocation: row.invoiceLocation ?? "-",
        status: row.status ?? "-",
        value: typeof row.value === "number" ? row.value : Number(row.value) || 0,
        sellerCode: row.sellerCode ?? "-",
      })),
    [salesTable]
  );

  const currencyTick = (v) =>
    (Number(v) || 0).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
      maximumFractionDigits: 0,
    });

  return (
    <Box>
      {/* KPIs — mesma altura */}
      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="stretch">
        <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
          <Card sx={CARD_STRETCH}>
            <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {isLoading ? (
                <Skeleton variant="circular" width={44} height={44} />
              ) : (
                <Avatar sx={{ bgcolor: COLORS.primary, width: 44, height: 44 }}>
                  <AttachMoneyIcon />
                </Avatar>
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1 }} noWrap>
                    Faturamento Total
                  </Typography>
                  {!isLoading && (
                    <AiBtn
                      cfg={{
                        metric: "total_revenue",
                        metricName: "Faturamento Total",
                        metricDefinition: "Receita total no período selecionado (impacta diretamente o resultado financeiro).",
                        metricValue: cards.totalRevenue || 0,
                        metricHuman: BRL(cards.totalRevenue || 0),
                      }}
                    />
                  )}
                </Stack>
                {isLoading ? (
                  <Skeleton height={36} width="60%" />
                ) : (
                  <Typography variant="h4" sx={{ color: COLORS.primary }}>
                    {BRL(cards.totalRevenue || 0)}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  Período selecionado
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
          <Card sx={CARD_STRETCH}>
            <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {isLoading ? (
                <Skeleton variant="circular" width={44} height={44} />
              ) : (
                <Avatar sx={{ bgcolor: COLORS.accent, width: 44, height: 44 }}>
                  <ShoppingCartIcon />
                </Avatar>
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1 }} noWrap>
                    Ticket Médio
                  </Typography>
                  {!isLoading && (
                    <AiBtn
                      cfg={{
                        metric: "avg_ticket",
                        metricName: "Ticket Médio",
                        metricDefinition: "Valor médio por pedido (impacta faturamento e margem por venda).",
                        metricValue: cards.avgTicket || 0,
                        metricHuman: BRL(cards.avgTicket || 0),
                      }}
                    />
                  )}
                </Stack>
                {isLoading ? (
                  <Skeleton height={36} width="60%" />
                ) : (
                  <Typography variant="h4" sx={{ color: COLORS.accent }}>
                    {BRL(cards.avgTicket || 0)}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  por pedido
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
          <Card sx={CARD_STRETCH}>
            <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {isLoading ? (
                <Skeleton variant="circular" width={44} height={44} />
              ) : (
                <Avatar sx={{ bgcolor: COLORS.warm, width: 44, height: 44 }}>
                  <Inventory2Icon />
                </Avatar>
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1 }} noWrap>
                    Produtos Vendidos
                  </Typography>
                  {!isLoading && (
                    <AiBtn
                      cfg={{
                        metric: "sold_units",
                        metricName: "Produtos Vendidos",
                        metricDefinition: "Quantidade total de unidades vendidas no período (impacta giro e planejamento de estoque).",
                        metricValue: cards.soldUnits || 0,
                        metricHuman: String(cards.soldUnits || 0),
                      }}
                    />
                  )}
                </Stack>
                {isLoading ? (
                  <Skeleton height={36} width="60%" />
                ) : (
                  <Typography variant="h4" sx={{ color: COLORS.warm }}>
                    {String(cards.soldUnits || 0)}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  Período selecionado
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
          <Card sx={CARD_STRETCH}>
            <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {isLoading ? (
                <Skeleton variant="circular" width={44} height={44} />
              ) : (
                <Avatar sx={{ bgcolor: COLORS.warn, width: 44, height: 44 }}>
                  <StorefrontIcon />
                </Avatar>
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1 }} noWrap>
                    Canais de Venda
                  </Typography>
                  {!isLoading && (
                    <AiBtn
                      cfg={{
                        metric: "channels_active",
                        metricName: "Canais de Venda",
                        metricDefinition: "Quantidade de canais ativos que geraram vendas no período (impacta distribuição e dependência de canais).",
                        metricValue: cards.channels || 0,
                        metricHuman: String(cards.channels || 0),
                      }}
                    />
                  )}
                </Stack>
                {isLoading ? (
                  <Skeleton height={36} width="60%" />
                ) : (
                  <Typography variant="h4" sx={{ color: COLORS.warn }}>
                    {String(cards.channels || 0)}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  ativos
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Gráficos (pastéis) */}
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <Card sx={CHART_CARD}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>
                  Receita por Categoria
                </Typography>
                {!isLoading && (
                  <AiBtn
                    cfg={{
                      metric: "revenue_by_category",
                      metricName: "Receita por Categoria",
                      metricDefinition: "Receita distribuída por categoria para identificar as que mais puxam faturamento e onde investir.",
                      metricValue: revenueByCategory,
                      metricHuman: "",
                    }}
                  />
                )}
              </Stack>
              {isLoading ? (
                <Skeleton variant="rectangular" height={300} />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={revenueByCategory}
                    margin={{ top: 8, right: 16, bottom: 56, left: 48 }}
                    barCategoryGap="18%"
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="category"
                      interval={0}
                      tick={<WrapTick />}
                      tickMargin={12}
                      padding={{ left: 16, right: 16 }}
                    />
                    <YAxis
                      width={64}
                      tickMargin={8}
                      tickFormatter={currencyTick}
                      domain={[0, "dataMax + 50000"]}
                    />
                    <Tooltip
                      formatter={(v) => [BRL(v), "Receita"]}
                      labelFormatter={(l) => `Categoria: ${l}`}
                    />
                    <Bar dataKey="revenue" name="Receita (R$)">
                      {revenueByCategory.map((_, i) => (
                        <Cell key={i} fill={CHART_SERIES[i % CHART_SERIES.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <Card sx={CHART_CARD}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>
                  Vendas por Canal (%)
                </Typography>
                {!isLoading && (
                  <AiBtn
                    cfg={{
                      metric: "sales_by_channel",
                      metricName: "Vendas por Canal (%)",
                      metricDefinition: "Participação percentual de vendas por canal (útil para balancear risco e investimento).",
                      metricValue: salesByChannel,
                      metricHuman: "",
                    }}
                  />
                )}
              </Stack>
              {isLoading ? (
                <Skeleton variant="rectangular" height={300} />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={salesByChannel} dataKey="value" nameKey="name" outerRadius={100} label>
                      {salesByChannel.map((_, i) => (
                        <Cell key={i} fill={CHART_SERIES[i % CHART_SERIES.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v, n) => [`${v}%`, n]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sx={{ display: "flex" }}>
          <Card sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>
                  Evolução Mensal — Pedidos e Receita
                </Typography>
                {!isLoading && (
                  <AiBtn
                    cfg={{
                      metric: "monthly_overview",
                      metricName: "Evolução Mensal — Pedidos e Receita",
                      metricDefinition: "Tendência mensal de pedidos e receita para entender sazonalidade e impacto de campanhas.",
                      metricValue: monthlyOverview,
                      metricHuman: "",
                    }}
                  />
                )}
              </Stack>
              {isLoading ? (
                <Skeleton variant="rectangular" height={300} />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={monthlyOverview}
                    margin={{ top: 8, right: 80, bottom: 32, left: 12 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tickMargin={10} />
                    <YAxis yAxisId="left" width={36} tickFormatter={(v) => Math.round(v)} />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      width={80}
                      tickFormatter={(v) =>
                        (Number(v) || 0).toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                          maximumFractionDigits: 0,
                        })
                      }
                    />
                    <Tooltip
                      formatter={(val, key) =>
                        key === "orders" ? [Math.round(val), "Pedidos"] : [BRL(val), "Receita"]
                      }
                    />
                    <Legend verticalAlign="bottom" height={28} />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="orders"
                      name="Pedidos (un)"
                      stroke={CHART_SERIES[1]}
                      dot={false}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="revenue"
                      name="Receita (R$)"
                      stroke={CHART_SERIES[0]}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sx={{ display: "flex" }}>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
                gap={1}
                sx={{ mb: 2 }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ flex: 1 }}>Detalhamento das Vendas</Typography>
                  {!isLoading && (
                    <AiBtn
                      cfg={{
                        metric: "sales_table",
                        metricName: "Detalhamento das Vendas",
                        metricDefinition: "Tabela detalhada de pedidos para analisar padrões (status, locais, valores) e melhorar conversão/execução.",
                        metricValue: { sample: salesGridRows.slice(0, 25) },
                        metricHuman: "",
                      }}
                    />
                  )}
                </Stack>
                <Button
                  variant="contained"
                  startIcon={<TableChartIcon />}
                  onClick={() => exportSalesToExcel(salesTable, start, end)}
                  disabled={isLoading || !salesTable?.length}
                  sx={{ textTransform: "none" }}
                >
                  Exportar Excel
                </Button>
              </Stack>

              {isLoading ? (
                <Skeleton variant="rectangular" height={360} />
              ) : (
                <Box sx={{ width: "100%", minHeight: 360 }}>
                  <DataGrid
                    rows={salesGridRows}
                    columns={salesGridColumns}
                    getRowId={(r) => r.id}
                    pageSizeOptions={[10, 25, 50, 100]}
                    initialState={{
                      pagination: { paginationModel: { page: 0, pageSize: 25 } },
                      sorting: { sortModel: [{ field: "orderNumber", sort: "desc" }] },
                    }}
                    localeText={localeTextPTBR}
                    autoHeight
                    disableRowSelectionOnClick
                    sx={{
                      "& .MuiDataGrid-columnHeaders": { bgcolor: "#F5F7FA" },
                      "& .MuiDataGrid-cell": { alignItems: "center" },
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {error && (
        <Typography sx={{ mt: 2 }} color="error">
          Erro ao carregar dados: {String(error?.message || error)}
        </Typography>
      )}

      <MetricAiInsightsDialog
        open={metricDialogOpen}
        metric={metricDialogMetric}
        metricName={metricDialogName}
        metricLabel={metricDialogLabel}
        metricHuman={metricDialogHuman}
        period={{ start, end }}
        loading={metricInsightsMutation.isLoading}
        error={metricDialogError}
        data={metricDialogData}
        scope="vendas"
        basePayload={metricDialogBasePayload}
        onClose={() => setMetricDialogOpen(false)}
      />
    </Box>
  );
};

export default VendaProdutos;
